<template>
  <div class="rdd-page">
    <!-- Breadcrumb + Latest Update -->
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="rdd-breadcrumb">
        <a href="javascript:void(0)" class="rdd-breadcrumb__back" @click="goBack">
          <i class="fas fa-chevron-left rdd-breadcrumb__chevron"></i>
          <span>Back</span>
        </a>
        <span class="rdd-breadcrumb__sep">| Review Domain</span>
        <i class="fas fa-chevron-right rdd-breadcrumb__arrow"></i>
        <span class="rdd-breadcrumb__current">{{ domainInfo.domain || '-' }}</span>
      </div>
      <!-- <div class="d-flex align-items-center" style="gap: 6px;">
        <span style="font-size: 14px; font-weight: 500; color: #4b4b4b; white-space: nowrap;">
          Latest Update: {{ latestUpdate || '-' }}
        </span>
      </div> -->
    </div>

    <!-- Banner Ignored (UR-09): tampil hanya bila ignore_info tidak null -->
    <div v-if="!loading && domainInfo.ignore_info" class="rdd-banner">
      <i class="fas fa-eye-slash rdd-banner__icon"></i>
      <div class="rdd-banner__title">
        This domain is ignored. It is not counted in domain monitoring and does not trigger Telegram alerts.
        Scans are still recorded.
      </div>
      <div class="rdd-banner__meta">
        <span><b>Ignored by:</b> {{ domainInfo.ignore_info.ignored_by || '-' }}</span>
        <span><b>Time (WIB):</b> {{ domainInfo.ignore_info.ignored_at || '-' }}</span>
        <span><b>Source:</b> {{ domainInfo.ignore_info.source_label || '-' }}</span>
        <span class="rdd-banner__reason"><b>Reason:</b> {{ domainInfo.ignore_info.reason || '-' }}</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center mt-5 mb-5">
      <h4>Loading...</h4>
    </div>

    <template v-else>
      <!-- Domain Information Card -->
      <div class="rdd-card mb-3">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <span class="rdd-card__title">Domain Information</span>
          <div class="d-flex" style="gap: 8px;">
            <button v-if="isWatchlistable(domainInfo.domain_status)" class="rdd-btn-watchlist"
              @click="onWatchlist(domainInfo)">Add to Watchlist</button>
            <button v-if="canManageIgnore && !isIgnored" class="rdd-btn-ignore"
              @click="onIgnore">Ignore</button>
            <button v-if="canManageIgnore && isIgnored" class="rdd-btn-unignore"
              @click="onUnignore">Unignore</button>
          </div>
        </div>
        <div class="d-flex flex-wrap" style="gap: 24px;">
          <div class="rdd-info-field">
            <div class="rdd-info-field__label">Domain</div>
            <div class="rdd-info-field__value">{{ domainInfo.domain || '-' }}</div>
          </div>
          <div class="rdd-info-field">
            <div class="rdd-info-field__label">First Scan</div>
            <div class="rdd-info-field__value">{{ formatDate(domainInfo.first_scan) || '-' }}</div>
          </div>
          <div class="rdd-info-field">
            <div class="rdd-info-field__label">Last Scan</div>
            <div class="rdd-info-field__value">{{ formatDate(domainInfo.last_scan) || '-' }}</div>
          </div>
          <div class="rdd-info-field">
            <div class="rdd-info-field__label">Risk Score</div>
            <div class="mt-1">
              <span class="rdd-badge" :class="riskClass(domainInfo.risk_score)">{{ domainInfo.risk_score || '-'
              }}</span>
            </div>
          </div>
          <div class="rdd-info-field">
            <div class="rdd-info-field__label">Domain Status</div>
            <div class="mt-1">
              <span class="rdd-badge" :class="statusClass(domainInfo.domain_status)">{{ domainInfo.domain_status || '-'
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- KPI Cards -->
      <div class="d-flex mb-3" style="gap: 16px;">
        <div class="rdd-kpi-card">
          <div class="rdd-kpi-card__label">Total Scan</div>
          <div class="rdd-kpi-card__value">{{ numberWithCommas(summary.total_scan) }}</div>
        </div>
        <!-- <div class="rdd-kpi-card">
          <div class="rdd-kpi-card__label">Total Device ID</div>
          <div class="rdd-kpi-card__value">{{ numberWithCommas(summary.total_device_id) }}</div>
        </div> -->
        <div class="rdd-kpi-card">
          <span>
            <span class="rdd-kpi-card__label">Device Location</span>
            <i class="fal fa-info-circle" id="location-kpi"></i>
            <b-popover target="location-kpi" triggers="hover" placement="top">
              Based on the number of devices with valid latitude and longitude data during the scan.
            </b-popover>
          </span>
          <div class="rdd-kpi-card__value">{{ numberWithCommas(summary.location) }}</div>
        </div>
        <div class="rdd-kpi-card">
          <div class="rdd-kpi-card__label">District</div>
          <div class="rdd-kpi-card__value">{{ numberWithCommas(summary.district) }}</div>
        </div>
      </div>

      <div class="rdd-card mb-3">
        <span>
          <span class="rdd-card__title mb-3 mr-1">Scan Trend</span>
          <i class="fal fa-info-circle" id="scan-trend"></i>
          <b-popover target="scan-trend" triggers="hover" placement="top">
            Data form Last 12 Months / Rolling 12 Months
          </b-popover>
        </span>
        <div ref="scanTrendChart" style="width: 100%; height: 200px;"></div>
      </div>

      <!-- District Table -->
      <div class="rdd-card mb-3">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <span class="rdd-card__title">District</span>
          <div class="rdd-search">
            <i class="fas fa-search rdd-search__icon"></i>
            <input
              type="text"
              class="rdd-search__input"
              placeholder="Search district"
              v-model="districtSearch"
              @input="onDistrictSearchInput"
            />
          </div>
        </div>

        <div class="table-responsive">
          <table class="rdd-table">
            <thead>
              <tr>
                <th>District</th>
                <th>Regency</th>
                <th>Province</th>
                <th>Total Scan</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isDistrictLoading">
                <td colspan="5" class="text-center text-muted py-4">Loading...</td>
              </tr>
              <tr v-else-if="districtList.length === 0">
                <td colspan="5" class="text-center text-muted py-4">No district data found.</td>
              </tr>
              <tr v-for="(item, index) in districtList" :key="index" v-else>
                <td>{{ item.district }}</td>
                <td>{{ item.regency }}</td>
                <td>{{ item.province }}</td>
                <td>{{ item.total_scan }}</td>
                <td class="text-right">
                  <a href="javascript:void(0)" class="rdd-table__link" @click="onDistrictDetail(item)">Detail</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex align-items-center justify-content-between rdd-pagination">
          <div class="d-flex align-items-center rdd-pagination__perpage">
            <span>View</span>
            <select v-model.number="districtPerPage" @change="onDistrictPerPageChange">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </select>
            <span>items per page</span>
          </div>
          <div class="d-flex align-items-center rdd-pagination__info">
            <span>Showing {{ districtShowingFrom }}-{{ districtShowingTo }} of {{ districtTotalData }} Items</span>
            <button class="rdd-pagination__arrow" :disabled="districtPage <= 1" @click="goToDistrictPage(districtPage - 1)">
              <i class="fas fa-chevron-left"></i>
            </button>
            <button class="rdd-pagination__arrow" :disabled="districtPage >= districtTotalPages" @click="goToDistrictPage(districtPage + 1)">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Activity History -->
      <div class="rdd-card">
        <div class="rdd-card__title mb-3">Activity History</div>
        <div class="rdd-timeline">
          <div v-for="(item, index) in activityHistory" :key="index" class="rdd-timeline__item">
            <div class="rdd-timeline__left">
              <div class="rdd-timeline__dot" :style="{ background: eventDotColor(item.event_key) }"></div>
              <div v-if="index < activityHistory.length - 1" class="rdd-timeline__line"></div>
            </div>
            <div class="rdd-timeline__content">
              <div class="rdd-timeline__time">{{ formatDate(item.event_time) }}</div>
              <div class="rdd-timeline__message">{{ item.message }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Watchlist Confirmation Modal -->
    <b-modal v-model="watchlistModal.show" centered size="sm" hide-header hide-footer>
      <div class="watchlist-modal">
        <div class="watchlist-modal__icon">
          <img :src="require('@/assets/icons/domain-detection/watchlist-warning.svg')" width="109" height="109" />
        </div>
        <div class="watchlist-modal__body">
          <p class="watchlist-modal__title">Watchlist This Domain?</p>
          <p class="watchlist-modal__subtitle">This domain's status will be changed to Watchlisted for ongoing
            monitoring.
          </p>
        </div>
        <div class="watchlist-modal__footer">
          <button class="watchlist-modal__btn watchlist-modal__btn--cancel" @click="watchlistModalHide">Cancel</button>
          <button class="watchlist-modal__btn watchlist-modal__btn--confirm" :disabled="watchlistModal.loading"
            @click="addToWatchlist">
            <span v-if="watchlistModal.loading">
              <b-spinner small></b-spinner>
            </span>
            <span v-else>Add to Watchlist</span>
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Watchlist Success Modal -->
    <b-modal v-model="successModal.show" centered size="sm" hide-header hide-footer>
      <div class="watchlist-modal">
        <div class="watchlist-modal__icon">
          <img :src="require('@/assets/icons/domain-detection/watchlist-success.svg')" width="134" height="134" />
        </div>
        <div class="watchlist-modal__body">
          <p class="watchlist-modal__title">Successfully Watchlisted Domain</p>
          <p class="watchlist-modal__subtitle">Domain successfully added to watchlist</p>
        </div>
        <div class="watchlist-modal__footer">
          <button class="watchlist-modal__btn watchlist-modal__btn--confirm"
            @click="successModal.show = false">OK</button>
        </div>
      </div>
    </b-modal>

    <!-- Ignore / Unignore Modal (shared component) -->
    <ignore-domain-modal v-model="ignoreModal.show" :mode="ignoreModal.mode"
      :domain-name="domainInfo.domain" :risk-score="domainInfo.risk_score"
      :is-public-platform="!!domainInfo.is_public_platform"
      :loading="ignoreModal.loading" @confirm="ignoreConfirm" />
  </div>

</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import IgnoreDomainModal from './children/IgnoreDomainModal.vue'

am4core.useTheme(am4themes_animated)

export default {
  metaInfo: {
    title: 'Domain Detection - Review Domain Detail',
  },
  components: { IgnoreDomainModal },
  data() {
    return {
      loading: false,
      latestUpdate: null,
      domainInfo: {},
      summary: {
        total_scan: 0,
        total_device_id: 0,
        location: 0,
        district: 0,
      },
      scanTrend: [],
      activityHistory: [],
      chart: null,
      watchlistModal: {
        show: false,
        item: null,
        loading: false,
      },
      successModal: {
        show: false,
      },
      ignoreModal: { show: false, mode: 'ignore', loading: false },

      districtSearch: '',
      districtSearchTimer: null,
      districtPage: 1,
      districtPerPage: 10,
      districtList: [],
      districtTotalData: 0,
      districtTotalPages: 1,
      isDistrictLoading: false,
    }
  },
  computed: {
    districtShowingFrom() {
      if (this.districtTotalData === 0) return 0
      return (this.districtPage - 1) * this.districtPerPage + 1
    },
    districtShowingTo() {
      return Math.min(this.districtPage * this.districtPerPage, this.districtTotalData)
    },
    isIgnored() {
      const s = (this.domainInfo.domain_status || '').toLowerCase().replace(/ /g, '_')
      return s === 'ignored'
    },
    // Samakan dengan ReviewDomain.vue: API hanya bisa diakses ADMIN & SUPER ADMIN.
    canManageIgnore() {
      return true
    },
  },
  mounted() {
    this.fetchDetail()
    this.fetchDistrictList()
    this.fetchLatestUpdate()
  },
  methods: {
    async fetchDetail(silent = false) {
      if (!silent) this.loading = true
      try {
        const resp = await this.$http.get('/api/domain-detection-review-domain/detail', {
          params: { domain_id: this.$route.params.domain_id },
        })
        const data = resp.data.data || {}
        this.domainInfo = data.domain_information || {}
        this.summary = data.summary || {}
        this.scanTrend = data.scan_trend_last_12_months || []
        this.activityHistory = data.activity_history || []

        if (this.domainInfo && (this.domainInfo.first_scan || this.domainInfo.last_scan)) {
          this.latestUpdate = this.formatFirstScanDate(this.domainInfo.first_scan || this.domainInfo.last_scan)
        }
      } catch (err) {
        this.errorPrint(err)
      } finally {
        this.loading = false
        this.$nextTick(() => this.buildChart())
      }
    },
    async fetchLatestUpdate() {
      try {
        const resp = await this.$http.get('/api/domain-detection-domain-distribution/domain-list', {
          params: { page: 1, limit: 100 },
        })
        const resData = resp.data && resp.data.data ? resp.data.data : (resp.data || {})
        const rowsData = Array.isArray(resData) ? resData : (resData.data || resData.details || [])

        let latestTime = null
        let latestRaw = null

        const topLevelCandidate = (resp.data && (resp.data.latest_update || resp.data.first_scan)) ||
          (resData && (resData.latest_update || resData.first_scan))
        if (topLevelCandidate) {
          const cleanTop = typeof topLevelCandidate === 'string' ? topLevelCandidate.replace(' | ', ' ') : topLevelCandidate
          const d = new Date(cleanTop)
          if (!isNaN(d.getTime())) {
            latestTime = d.getTime()
            latestRaw = topLevelCandidate
          }
        }

        if (Array.isArray(rowsData)) {
          rowsData.forEach((row) => {
            const raw = row.first_scan || row.firstScan || row.created_at || row.date
            if (raw) {
              const cleanRaw = typeof raw === 'string' ? raw.replace(' | ', ' ') : raw
              const d = new Date(cleanRaw)
              if (!isNaN(d.getTime())) {
                const time = d.getTime()
                if (latestTime === null || time > latestTime) {
                  latestTime = time
                  latestRaw = raw
                }
              }
            }
          })
        }

        if (latestRaw) {
          this.latestUpdate = this.formatFirstScanDate(latestRaw)
        } else if (!this.latestUpdate) {
          this.latestUpdate = '-'
        }
      } catch (err) {
        if (!this.latestUpdate) this.latestUpdate = '-'
      }
    },
    formatFirstScanDate(dateStr) {
      if (!dateStr) return '-'
      const cleanStr = typeof dateStr === 'string' ? dateStr.replace(' | ', ' ') : dateStr
      const date = new Date(cleanStr)
      if (isNaN(date.getTime())) return dateStr

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      return `${year}-${month}-${day} | ${hours}:${minutes}:${seconds}`
    },
    async fetchDistrictList() {
      this.isDistrictLoading = true
      try {
        const resp = await this.$http.get('/api/domain-detection-summary/district-table', {
          params: {
            domain: this.$route.params.domain_id,
            page: this.districtPage,
            limit: this.districtPerPage,
            search: this.districtSearch || undefined,
          },
        })
        const payload = resp.data.data || {}
        this.districtList = payload.data || []
        const pagination = payload.pagination || {}
        this.districtTotalData = pagination.total_data || 0
        this.districtTotalPages = pagination.total_page || 1
      } catch (err) {
        this.districtList = []
        this.districtTotalData = 0
        this.districtTotalPages = 1
        this.errorPrint(err)
      } finally {
        this.isDistrictLoading = false
      }
    },

    /* ---------- Ignore / Unignore ---------- */
    onIgnore() {
      this.ignoreModal = { show: true, mode: 'ignore', loading: false }
    },
    onUnignore() {
      this.ignoreModal = { show: true, mode: 'unignore', loading: false }
    },
    async ignoreConfirm(reason) {
      if (this.ignoreModal.loading) return
      const isIgnore = this.ignoreModal.mode === 'ignore'
      const name = this.domainInfo.domain
      this.ignoreModal.loading = true
      try {
        const base = '/api/domain-detection-review-domain'
        if (isIgnore) {
          await this.$http.post(`${base}/ignore`, { domain_id: this.domainInfo.domain_id, reason: reason || '' })
        } else {
          await this.$http.post(`${base}/unignore`, { domain_id: this.domainInfo.domain_id })
        }
        this.ignoreModal.show = false
        this.$bvToast.toast(
          isIgnore
            ? 'It will not be counted in domain monitoring or sent to Telegram alerts.'
            : 'New scans will be counted and can trigger Telegram alerts.',
          {
            title: isIgnore ? `${name} has been ignored` : `${name} is monitored again`,
            variant: 'success', solid: true, autoHideDelay: 4000,
          },
        )
        await this.fetchDetail(true)
      } catch (err) {
        this.$bvToast.toast(
          (err && err.response && err.response.data && err.response.data.message) || 'Please try again.',
          {
            title: isIgnore ? 'Failed to ignore domain. Please try again.' : 'Failed to unignore domain. Please try again.',
            variant: 'danger', solid: true, autoHideDelay: 5000,
          },
        )
        // 400 "already ignored" / "not ignored" = data basi -> segarkan
        if (err && err.response && err.response.status === 400) {
          this.ignoreModal.show = false
          await this.fetchDetail()
        }
      } finally {
        this.ignoreModal.loading = false
      }
    },

    onDistrictSearchInput() {
      if (this.districtSearchTimer) clearTimeout(this.districtSearchTimer)
      this.districtSearchTimer = setTimeout(() => {
        this.districtPage = 1
        this.fetchDistrictList()
      }, 400)
    },
    onDistrictPerPageChange() {
      this.districtPage = 1
      this.fetchDistrictList()
    },
    goToDistrictPage(page) {
      if (page < 1 || page > this.districtTotalPages) return
      this.districtPage = page
      this.fetchDistrictList()
    },
    buildChart() {
      if (this.chart) this.chart.dispose()
      const chart = am4core.create(this.$refs.scanTrendChart, am4charts.XYChart)
      chart.paddingRight = 20

      const monthMap = {
        Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April',
        May: 'May', Jun: 'June', Jul: 'July', Aug: 'August',
        Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December'
      }

      chart.data = this.scanTrend.map(d => ({
        month: d.month,
        fullMonth: monthMap[d.month] || d.month,
        value: d.total_scan,
      }))

      const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis())
      categoryAxis.dataFields.category = 'month'
      categoryAxis.renderer.grid.template.disabled = true
      categoryAxis.renderer.labels.template.fill = am4core.color('#9d9d9d')
      categoryAxis.renderer.labels.template.fontSize = 10

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
      valueAxis.renderer.grid.template.stroke = am4core.color('#f5f5f5')
      valueAxis.renderer.labels.template.fill = am4core.color('#9d9d9d')
      valueAxis.renderer.labels.template.fontSize = 10
      valueAxis.min = 0

      const series = chart.series.push(new am4charts.LineSeries())
      series.dataFields.valueY = 'value'
      series.dataFields.categoryX = 'month'
      series.stroke = am4core.color('#267CE5')
      series.strokeWidth = 2
      series.tensionX = 0.8

      series.tooltipText = '[bold]{fullMonth}[/]\n{value} scan'

      series.tooltip.background.fill = am4core.color('#4b4b4b')
      series.tooltip.background.strokeWidth = 0
      series.tooltip.label.fill = am4core.color('#ffffff')
      series.tooltip.label.fontSize = 12

      const bullet = series.bullets.push(new am4charts.CircleBullet())
      bullet.circle.fill = am4core.color('#267CE5')
      bullet.circle.radius = 3

      const hoverState = bullet.states.create('hover')
      hoverState.properties.scale = 1.8

      chart.cursor = new am4charts.XYCursor()
      chart.cursor.lineY.disabled = true
      chart.cursor.xAxis = categoryAxis

      this.chart = chart
    },
    goBack() {
      this.$router.push({ name: 'ReviewDomain' })
    },
    async addToWatchlist() {
      this.watchlistModal.loading = true
      try {
        await this.$http.post('/api/domain-detection-review-domain/update-watchlist', {
          domain_id: this.domainInfo.domain_id,
          watchlist: true,
        })
        this.watchlistModal.show = false
        this.watchlistModal.item = null
        this.successModal.show = true
        this.fetchDetail()
      } catch (err) {
        this.errorPrint(err)
      } finally {
        this.watchlistModal.loading = false
      }
    },
    formatDate(val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d)) return val
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} | ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    formatDateOnly(val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d)) return val
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    isWatchlistable(status) {
      if (!status) return true
      const s = status.toLowerCase()
      return s !== 'watchlisted' && s !== 'dismissed' && s !== 'ignored'
    },
    riskClass(score) {
      if (!score) return ''
      const s = score.toLowerCase()
      if (s === 'high') return 'rdd-badge--high'
      if (s === 'medium') return 'rdd-badge--medium'
      if (s === 'low') return 'rdd-badge--low'
      return ''
    },
    statusClass(status) {
      if (!status) return ''
      const s = status.toLowerCase().replace(/ /g, '_')
      if (s === 'under_review') return 'rdd-badge--under-review'
      if (s === 'watchlisted') return 'rdd-badge--watchlisted'
      if (s === 'dismissed') return 'rdd-badge--dismissed'
      if (s === 'ignored') return 'rdd-badge--ignored'
      return ''
    },
    eventDotColor(eventKey) {
      const map = {
        scan_count_milestone: '#D92D20',
        watchlist_added: '#F04438',
        risk_score_high: '#FDB022',
        risk_score_medium: '#267CE5',
        scan_patterns_identified: '#FDB022',
        domain_registered: '#D0D0D0',
        domain_ignored: '#9d9d9d',
        domain_unignored: '#267CE5',
      }
      return map[eventKey] || '#267CE5'
    },
    onWatchlist(row) {
      this.watchlistModal.item = row
      this.watchlistModal.show = true
    },
    watchlistModalHide() {
      this.watchlistModal.show = false
      this.watchlistModal.item = null
    },
    onDistrictDetail(item) {
      this.$router.push({
        path: '/dashboard/domain-detection/domain-distribution',
        query: {
          district: item.district,
          regency: item.regency,
          province: item.province,
        },
      })
    },
  },
}
</script>

<style scoped>
.rdd-page {
  padding: 0;
}

/* Breadcrumb */
.rdd-breadcrumb {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  word-break: break-all;
  overflow-wrap: anywhere;
}

.rdd-breadcrumb__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6c6c6c;
  text-decoration: none;
  margin-right: 6px;
  vertical-align: middle;
}

.rdd-breadcrumb__back:hover {
  color: #4b4b4b;
  text-decoration: none;
}

.rdd-breadcrumb__chevron {
  font-size: 10px;
}

.rdd-breadcrumb__sep {
  color: #bebebe;
  margin-right: 6px;
  display: inline;
}

.rdd-breadcrumb__arrow {
  font-size: 10px;
  color: #bebebe;
  margin-right: 6px;
  display: inline;
  vertical-align: middle;
}

.rdd-breadcrumb__current {
  color: #4b4b4b;
  word-break: break-all;
  overflow-wrap: anywhere;
  display: inline;
  vertical-align: middle;
}

/* Card */
.rdd-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}

.rdd-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #4b4b4b;
}

/* Watchlist button */
.rdd-btn-watchlist {
  background: #267CE5;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 0 1.5px rgba(0, 0, 0, 0.1);
}

/* Ignore / Unignore buttons */
.rdd-btn-ignore {
  background: #fff;
  color: #D92D20;
  border: 1px solid #FDA29B;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.rdd-btn-unignore {
  background: #fff;
  color: #267CE5;
  border: 1px solid #267CE5;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

/* Ignored banner */
/* Banner Ignored (design 08): ikon + teks di kiri, metadata satu baris rata kanan */
.rdd-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #F2F2F2;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.rdd-banner__icon {
  flex-shrink: 0;
  font-size: 16px;
  color: #6c6c6c;
}

.rdd-banner__title {
  /* Bug 306/332: teks dampak tidak boleh menyempit walau Reason panjang */
  flex: 1 1 320px;
  min-width: 280px;
  font-size: 13px;
  font-weight: 600;
  color: #4b4b4b;
}

.rdd-banner__meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px 16px;
  flex: 0 1 auto;
  max-width: 50%;
  min-width: 0;
  margin-left: auto;
  font-size: 12px;
  color: #6c6c6c;
  text-align: right;
}

.rdd-banner__reason {
  /* Reason panjang (s/d 200 karakter) dibungkus, tidak merusak layout */
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

/* Layar sempit: metadata pindah ke bawah teks dampak */
@media (max-width: 991.98px) {
  .rdd-banner {
    flex-wrap: wrap;
  }
  .rdd-banner__title {
    flex-basis: calc(100% - 40px);
    min-width: 0;
  }
  .rdd-banner__meta {
    max-width: 100%;
    flex-basis: 100%;
    justify-content: flex-start;
    margin-left: 28px;
    text-align: left;
  }
}

/* Domain info fields */
.rdd-info-field {
  flex: 1 1 150px;
  min-width: 0;
}

.rdd-info-field__label {
  font-size: 12px;
  color: #9d9d9d;
  margin-bottom: 8px;
}

.rdd-info-field__value {
  font-size: 14px;
  font-weight: 500;
  color: #4b4b4b;
  word-break: break-all;
  overflow-wrap: anywhere;
}

/* Badges */
.rdd-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06px;
  white-space: nowrap;
}

.rdd-badge--high {
  background: #D92D20;
  color: #fff;
}

.rdd-badge--medium {
  background: #FDB022;
  color: #fff;
}

.rdd-badge--low {
  background: #12B76A;
  color: #fff;
}

.rdd-badge--under-review {
  background: #FFF9F5;
  color: #ec911b;
}

.rdd-badge--watchlisted {
  background: #FFFBFA;
  color: #F04438;
}

.rdd-badge--dismissed {
  background: #FAFAFA;
  color: #4B4B4B;
}

.rdd-badge--ignored {
  background: #EAEAEA;
  color: #6c6c6c;
}

/* KPI Cards */
.rdd-kpi-card {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 8px;
  padding: 20px 16px;
}

.rdd-kpi-card__label {
  font-size: 11px;
  color: #9d9d9d;
  margin-bottom: 8px;
  margin-right: 8px;
}

.rdd-kpi-card__value {
  font-size: 28px;
  font-weight: 700;
  color: #4b4b4b;
  line-height: 1;
}

/* District search */
.rdd-search {
  position: relative;
  width: 240px;
}

.rdd-search__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: #bebebe;
}

.rdd-search__input {
  width: 100%;
  padding: 6px 12px 6px 30px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  font-size: 12px;
  color: #4b4b4b;
  outline: none;
}

.rdd-search__input:focus {
  border-color: #267CE5;
}

/* District table */
.rdd-table {
  width: 100%;
  border-collapse: collapse;
}

.rdd-table thead th {
  text-align: left;
  font-size: 11px;
  font-weight: 500;
  color: #9d9d9d;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
}

.rdd-table tbody td {
  font-size: 13px;
  color: #4b4b4b;
  padding: 12px;
  border-bottom: 1px solid #f5f5f5;
}

.rdd-table__link {
  color: #267CE5;
  text-decoration: underline;
  font-size: 12px;
  font-weight: 500;
}

/* Pagination */
.rdd-pagination {
  margin-top: 12px;
  font-size: 12px;
  color: #9d9d9d;
}

.rdd-pagination__perpage {
  gap: 8px;
}

.rdd-pagination__perpage select {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
  color: #4b4b4b;
  outline: none;
}

.rdd-pagination__info {
  gap: 10px;
}

.rdd-pagination__arrow {
  border: 1px solid #e8e8e8;
  background: #fff;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  font-size: 10px;
  color: #4b4b4b;
  cursor: pointer;
}

.rdd-pagination__arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Timeline */
.rdd-timeline {
  display: flex;
  flex-direction: column;
}

.rdd-timeline__item {
  display: flex;
  gap: 12px;
}

.rdd-timeline__left {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 9px;
}

.rdd-timeline__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rdd-timeline__line {
  width: 1px;
  flex: 1;
  min-height: 24px;
  background: #e0e0e0;
  margin-top: 2px;
}

.rdd-timeline__content {
  padding-bottom: 20px;
}

.rdd-timeline__time {
  font-size: 10px;
  color: #9d9d9d;
  margin-bottom: 2px;
}

.rdd-timeline__message {
  font-size: 12px;
  color: #4b4b4b;
}

/* Watchlist / Success modals */
.watchlist-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 16px;
}

.watchlist-modal__icon {
  margin-bottom: 4px;
}

.watchlist-modal__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
  padding: 0 8px;
}

.watchlist-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
}

.watchlist-modal__subtitle {
  font-size: 14px;
  color: rgba(165, 165, 165, 0.87);
  margin: 0;
}

.watchlist-modal__footer {
  display: flex;
  gap: 16px;
  width: 100%;
  padding: 10px 0 0;
}

.watchlist-modal__btn {
  flex: 1;
  padding: 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
}

.watchlist-modal__btn--cancel {
  background: #fff;
  border: 1px solid #267ce5;
  color: #267ce5;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.1);
}

.watchlist-modal__btn--confirm {
  background: #267ce5;
  border: 1px solid #e8e8e8;
  color: #fff;
  box-shadow: 0 0 1.5px rgba(0, 0, 0, 0.1);
}

.watchlist-modal__btn--confirm:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>